import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const POLL_MS = 4000;
const MAX_POLLS = 90;
const EARLY_CHECKS = 2;

// Latest report for a job plus any generation in progress (auto-generated when the job is opened/imported, or Regenerate)
export const useJobReport = ({ jobId, fetchByJob, fetchStatus, start, name }) => {
  const [data, setData] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [checks, setChecks] = useState(0);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchByJob(jobId);
    setData(res.exists ? res : null);
    setRunningId(res.processing_job_id || null);
  }, [jobId, fetchByJob]);

  useEffect(() => {
    setLoading(true); setData(null); setRunningId(null); setFailed(false); setChecks(0);
    load().catch(() => {}).finally(() => setLoading(false));
  }, [load]);

  // A just-opened job queues its report a moment after the page loads, so look again before offering "Generate"
  useEffect(() => {
    if (loading || data || runningId || failed || checks >= EARLY_CHECKS) return undefined;
    const t = setTimeout(() => load().catch(() => {}).finally(() => setChecks((c) => c + 1)), 2500);
    return () => clearTimeout(t);
  }, [loading, data, runningId, failed, checks, load]);

  useEffect(() => {
    if (!runningId) return undefined;
    let polls = 0;
    const t = setInterval(async () => {
      polls += 1;
      try {
        const s = await fetchStatus(runningId);
        if (s.status !== 'completed' && s.status !== 'failed' && polls < MAX_POLLS) return;
        clearInterval(t);
        if (s.status !== 'completed') {
          setFailed(true);
          toast.error(s.error ? `${name} failed: ${s.error}` : `${name} generation failed — please try again`);
        }
        await load();
      } catch { /* keep polling */ }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [runningId, fetchStatus, load, name]);

  const generate = useCallback(async () => {
    setFailed(false);
    setStarting(true);
    try {
      const res = await start();
      if (res?.job_id) setRunningId(res.job_id);
    } catch {
      setFailed(true);
      toast.error(`Could not start ${name.toLowerCase()} generation`);
    } finally {
      setStarting(false);
    }
  }, [start, name]);

  return { data, running: !!runningId || starting, loading, failed, generate };
};

export default useJobReport;
